import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { useLenis } from "lenis/react";
import { site } from "../content";

const MaskedLine = ({ children, delay }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
      <motion.span
        className="block"
        initial={reduce ? false : { y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = () => {
  const lenis = useLenis();
  const reduce = useReducedMotion();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);
  const { hero } = site;

  const go = (e, id) => {
    e.preventDefault();
    lenis?.scrollTo(`#${id}`, { offset: -96, duration: 1.4 });
  };

  return (
    <section className="relative overflow-hidden pt-36 pb-20 md:pt-48 md:pb-32" data-testid="hero-section">
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-[#EFE6DC] blur-3xl opacity-70"
      />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center relative">
        <div className="lg:col-span-6">
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-xs uppercase tracking-[0.25em] font-semibold text-[#4A6572] mb-8"
            data-testid="hero-eyebrow"
          >
            {hero.eyebrow}
          </motion.p>

          <h1
            className="font-serif font-light text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.04] text-[#2B2724]"
            data-testid="hero-heading"
          >
            <MaskedLine delay={0.15}>{hero.titleLines[0]}</MaskedLine>
            <MaskedLine delay={0.28}>{hero.titleLines[1]}</MaskedLine>
            <MaskedLine delay={0.41}>
              <em className="italic text-[#7C8C7E]">{hero.titleLines[2]}</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 text-base md:text-lg leading-relaxed text-[#5C564F] max-w-lg"
            data-testid="hero-subtitle"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#yhteys"
              onClick={(e) => go(e, "yhteys")}
              className="group rounded-full bg-[#7C8C7E] text-[#FBF8F4] font-medium px-8 py-4 flex items-center gap-2 hover:bg-[#5E6D60] transition-colors duration-200"
              data-testid="hero-cta-contact"
            >
              {hero.primaryCta}
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href="#palvelut"
              onClick={(e) => go(e, "palvelut")}
              className="group rounded-full border-2 border-[#2B2724] text-[#2B2724] font-medium px-8 py-[0.9rem] flex items-center gap-2 hover:bg-[#2B2724] hover:text-[#FBF8F4] transition-colors duration-200"
              data-testid="hero-cta-services"
            >
              {hero.secondaryCta}
              <ArrowDown size={18} className="transition-transform duration-200 group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-12 text-xs uppercase tracking-[0.25em] text-[#5C564F] flex flex-wrap gap-x-4 gap-y-2"
            data-testid="hero-trust-row"
          >
            {hero.trust.map((t, i) => (
              <span key={t} className="flex items-center gap-4">
                {i > 0 && <span className="w-1.5 h-1.5 rounded-full bg-[#7C8C7E]" aria-hidden="true" />}
                {t}
              </span>
            ))}
          </motion.p>
        </div>

        <div className="lg:col-span-6" ref={imgRef}>
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: parallaxY }}
            className="relative max-w-md mx-auto lg:ml-auto"
          >
            <div
              className="grain aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#EFE6DC] shadow-[0_24px_60px_rgba(43,39,36,0.10)]"
              data-testid="hero-image"
            >
              <img
                src="/images/milja-hero.jpg"
                alt={hero.imageAlt}
                width={502}
                height={628}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
